import {TaskTypeEnum} from 'src/api/ProactApiClient';
import {GetUrlSectionFromTaskType} from 'src/app/tasks/taskHelpers';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export type FrontendLinkType =
  | 'Task'
  | 'CourseSession'
  | 'CourseAssignment'
  | 'CourseAssignmentAdmin'
  | 'Form'
  | 'ItemAssignment'
  | 'SafetyEvent'
  | 'Personnel'
  | 'Inspection';

function assertNever(x: never): never {
  throw new Error(`Unhandled link type: ${x}`);
}

function resolveTaskPath(
  taskDefinitionId: string,
  taskAssignmentId: string,
  taskType: TaskTypeEnum
): string | null {
  const section = GetUrlSectionFromTaskType(taskType);
  if (section === 'invalid') return null;

  if (taskType === TaskTypeEnum.Actions) {
    return PathUtility.ActionViewUser(taskDefinitionId, 'content');
  }

  return PathUtility.ViewTaskAssignment(taskType, taskAssignmentId);
}

export function resolveLinkPath(params: URLSearchParams): string | null {
  const type = params.get('type') as FrontendLinkType | null;
  if (!type) return null;

  switch (type) {
    case 'Task': {
      const taskDefinitionId = params.get('taskDefinitionId');
      const taskAssignmentId = params.get('taskAssignmentId');
      const taskTypeStr = params.get('taskType');
      if (!taskDefinitionId || !taskAssignmentId || !taskTypeStr) return null;
      const taskType = Number(taskTypeStr) as TaskTypeEnum;
      if (isNaN(taskType)) return null;
      return resolveTaskPath(taskDefinitionId, taskAssignmentId, taskType);
    }
    case 'CourseSession': {
      const sessionId = params.get('sessionId');
      if (!sessionId) return null;
      return PathUtility.TrainingSessionRecord(sessionId, 'overview');
    }
    case 'CourseAssignment': {
      const assignmentId = params.get('assignmentId');
      if (!assignmentId) return null;
      return PathUtility.TrainingCourseOverview(assignmentId);
    }
    case 'CourseAssignmentAdmin': {
      const courseDefinitionId = params.get('courseDefinitionId');
      const assignmentId = params.get('assignmentId');
      if (!courseDefinitionId || !assignmentId) return null;
      return PathUtility.TrainingCourseAssignmentView(
        courseDefinitionId,
        assignmentId,
        'content'
      );
    }
    case 'Form': {
      const formDefinitionId = params.get('formDefinitionId');
      const formId = params.get('formId');
      if (!formDefinitionId || !formId) return null;
      return PathUtility.FormSubmission(formDefinitionId, formId, 'content');
    }
    case 'ItemAssignment': {
      const definitionId = params.get('definitionId');
      const id = params.get('id');
      if (!definitionId || !id) return null;
      return PathUtility.ItemTrackerView(definitionId, id);
    }
    case 'SafetyEvent': {
      const id = params.get('id');
      if (!id) return null;
      return PathUtility.SafetyEventView(id, 'content');
    }
    case 'Personnel': {
      const id = params.get('id');
      if (!id) return null;
      return PathUtility.PersonnelView(id, 'overview');
    }
    case 'Inspection': {
      const id = params.get('id');
      if (!id) return null;
      return PathUtility.InspectionsView(id);
    }
    default:
      return assertNever(type);
  }
}
